import {
    Box,
    CircularProgress,
    FormControl,
    Grid,
    MenuItem,
    TextField,
} from "@material-ui/core";
import ReplyIcon from '@mui/icons-material/Reply';
import { Autocomplete, Button } from "@mui/material";
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Modal from '@mui/material/Modal';
import 'dayjs/locale/en-gb';
import { jwtDecode } from "jwt-decode";
import { MaterialReactTable } from "material-react-table";
import React, { useEffect, useMemo, useState } from 'react';
import Swal from "sweetalert2";
import PageTitle from "../../../components/PageTitle/PageTitle";
import axios from "../../../utils/axios";

const SettlementDocumentUpload = () => {
    // Get User ID from Token
    const token = localStorage.getItem("bearer-token");
    const decoded = jwtDecode(token);
    const userId = decoded.id;

    // State Management
    const [issuer, setIssuer] = useState("All");
    const [dividendType, setDividendType] = useState("All");
    const [tableData, setTableData] = useState([]);
    const [issuerList, setIssuerList] = useState([{ id: "All", displayName: "All (All Companies)" }]);
    const [loadingIssuers, setLoadingIssuers] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [error, setError] = useState(null);

    // Map Dividend Type to Numeric Values
    const getDividendTypeValue = (type) => {
        switch (type) {
            case "Cash": return "1";
            case "Stock": return "2";
            default: return "3";
        }
    };

    // Fetch Issuer List
    useEffect(() => {
        const fetchIssuerNames = async () => {
            setLoadingIssuers(true);
            try {
                const { data } = await axios.get("/issuer/get-info");
                if (data?.payload) {
                    const formattedIssuerList = data.payload.map((issuer) => ({
                        id: issuer.id,
                        displayName: `${issuer.name} (${issuer.tradecode})`,
                    }));
                    setIssuerList([{ id: "All", displayName: "All (All Companies)" }, ...formattedIssuerList]);
                }
            } catch (error) {
                console.error("Error fetching issuer names:", error);
            } finally {
                setLoadingIssuers(false);
            }
        };

        fetchIssuerNames();
    }, []);

    // Fetch Table Data
    useEffect(() => {
        const fetchTableData = async () => {
            setLoadingTable(true);
            setError(null);
            try {
                const payload = {
                    issuerId: issuer === "All" ? "All" : issuer,
                    dividendType: getDividendTypeValue(dividendType),
                    userId: userId,
                };

                const response = await axios.post("/settlement/get-by-settlement-document-upload-report", payload);
                if (response.data?.payload) {
                    setTableData(response.data.payload);
                } else {
                    setTableData([]);
                }
            } catch (error) {
                console.error("Error fetching table data:", error);
                setError("Failed to load data. Please try again.");
            } finally {
                setLoadingTable(false);
            }
        };

        fetchTableData();
    }, [issuer, dividendType, userId]);

    // Define Table Columns
    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'SL',
            size: 50,
            Cell: ({ row }) => row.index + 1,
           },
        { accessorKey: "issuername", header: "Issuer Name",size: 50  },
        { accessorKey: "investorname", header: "Investor Name",size: 50  },
        { accessorKey: "boid", header: "BO ID",size: 50  },
        { accessorKey: "foliono", header: "Folio No",size: 50  },
        {
            accessorKey: "dividendtype",
            header: "Dividend Type",
            size: 50 ,
            Cell: ({ cell }) => (cell.getValue() === "1" ? "Cash" : "Stock"),
        },
        { accessorKey: "amt", header: "Amount",size: 50  },
        { accessorKey: "settno", header: "Settlement No",size: 50  },
        {
            accessorKey: "settstatus",
            header: "Sett Status",
            size: 50 ,
            Cell: ({ cell }) => (cell.getValue() === 1 ? "Settled" : "Pending"),
        },
        { accessorKey: "settledate", header: "Settlement Date",size: 50 },
        {
            accessorKey: "uploaddoc",
            header: "Document",
            size: 50 ,
            Cell: ({ row }) => {
                // Local state to handle preview dialog visibility
                const [openPreview, setOpenPreview] = useState(false);
                const [filePreview, setFilePreview] = useState('');
                
                const style = {
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '60%',
                  bgcolor: 'background.paper',
                  border: '2px solid #eee',
                  boxShadow: '2px 2px 2px 2px #eee',
                  borderRadius:"10px",
                  p: 4
                };
          
          
                const handlePreviewClick = async () => {
                  try{
                  const fileUrlfromDB = row.original.uploaddoc[0];  // Access the file URL from the row
                  const fileURLclean = fileUrlfromDB.replace(/^\["|"\]$/g, '');
                  const response = await axios.post(`/settlement/get-settlement-file/${row.original.settlement_id}`,{ filename: fileURLclean },
                    { responseType: 'blob' });
                    const file = new Blob([response.data], { type: 'application/pdf' });
                    const fileUrl = URL.createObjectURL(file);
          
                  
                  if (fileUrl) {
                    setFilePreview(fileUrl); // Set the file preview URL
                    }
                    setOpenPreview(true); 
                  }
                  catch (error) {
                    console.error("Error fetching PDF:", error);
                
                    // Check if the error is a 404 Not Found
                    if (error.response && error.response.status === 404) {
                      Swal.fire({
                        icon: "warning",
                        title: "File Not Found",
                        text: "The requested PDF file was not uploaded on the server.",
                      });
                    } else {
                      // Handle other errors
                      Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: "Something went wrong while fetching the file!",
                      });
                    }
                  }
                };
                const openModal = () =>{
                  return(
                  <Modal
                  aria-labelledby="transition-modal-title"
                  aria-describedby="transition-modal-description"
                  open={openPreview}
                  onClose={() => setOpenPreview(false)}
                  closeAfterTransition
                  BackdropComponent={Backdrop}
                  slots={{ backdrop: Backdrop }}
                  slotProps={{
                  backdrop: {
                      timeout: 500,
                  }}}
                 style={{overflow: "auto", display:"flex",justifyContent:"center",alignItems:"center", textAlign:"center", borderRadius:"10px"
                 }}
                 >
                  <Fade in={openPreview}>
                  <Box sx={style} style={{textAlign:"center",width:"80%",height:"95vh"}} >
                      <h3>PDF VIEWER </h3>
                      
                      
                      
                      <Box className="form-control" style={{textAlign:"center",height:"90%"}}>
                        
                      {
                      // filePreview ? 
                      
                      filePreview ? (
                        <embed src={filePreview} width="100%" height="93%" type="application/pdf" />
                      ) 
                      :
                      (
                        <div
                            style={{
                                alignItems: 'center',
                                border: '2px dashed rgba(0, 0, 0, .3)',
                                fontSize: '2rem',
                                height: '100%',
                                display:"flex",
                                justifyContent: 'center',
                                width: '100%',
                                marginBottom:"5px"
                            }}
                        >
                            View Area<br/>
                            Oops! The file is not uploaded yet.
                        </div>
                    )
                    }
                          
                          
                          <Button type="button" onClick={()=>{setOpenPreview(false);}} variant="contained" color="info" style={{color:"white"}}><ReplyIcon/> Close</Button>
                          </Box>
          
                          
                       
                  </Box>
                  </Fade>
              </Modal>
                  )}
          
                return (
                  <>
                    <Button variant="contained" color="primary" onClick={handlePreviewClick}>
                      View
                    </Button>
                    {
                      openPreview && openModal()
                    }
                  </>
                );
              },
        },
        
        { accessorKey: "enrtyby", header: "Entry By",size: 50  },
        {
            accessorKey: "entrydate",
            header: "Entry Date",
            size: 50 ,
            Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString(),
        },
    ], []);

    return (
        <>
            <PageTitle title="Settlement Document Upload View Dashboard" />

            {/* Dropdown Filters */}
            <Grid container spacing={2} style={{ marginBottom: "20px" }}>
                {/* Issuer Dropdown */}
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <Autocomplete
                            options={issuerList}
                            getOptionLabel={(option) => option.displayName}
                            value={issuerList.find((item) => item.id === issuer) || null}
                            onChange={(_, newValue) => setIssuer(newValue ? newValue.id : "All")}
                            loading={loadingIssuers}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Issuer Name"
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingIssuers ? <CircularProgress size={24} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                </Grid>

                {/* Dividend Type Dropdown */}
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <TextField
                            select
                            label="Dividend Type"
                            value={dividendType}
                            onChange={(e) => setDividendType(e.target.value)}
                            variant="outlined"
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="Cash">Cash</MenuItem>
                            <MenuItem value="Stock">Stock</MenuItem>
                        </TextField>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Table */}
            {loadingTable ? (
                <CircularProgress size={40} style={{ display: "block", margin: "20px auto" }} />
            ) : error ? (
                <p style={{ textAlign: "center", color: "red" }}>{error}</p>
            ) : (
                <MaterialReactTable 
                columns={columns} data={tableData} 
                enablePagination
                enableSorting
                enableColumnFilters
                initialState={{ density: "compact" }}
                muiTableContainerProps={{sx:{maxHeight:"60vh"}}}
                muiTableBodyCellProps={{ sx: { border: "0.5px solid #e0e0e0" } }}
                muiTableHeadCellProps={{ sx: { border: "0.5px solid #e0e0e0" } }}
                muiTableFooterRowProps={{
                  sx: { fontWeight: "bold", backgroundColor: "#f1f1f1" },
                }}
               />
            )}
        </>
    );
};

export default SettlementDocumentUpload;
